import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mic, Plug, RefreshCw, Loader2, X, ExternalLink, CheckCircle2, Trash2, Sparkles, Calendar, Plus, Pencil, Check, Upload, FileText, Music, AlertCircle, ChevronLeft, ChevronRight, ArrowUpDown, ChevronDown, Search, Settings, Tag, Square, CheckSquare, MoreVertical, Folder, FolderPlus, FolderOpen, Edit2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';

// Use runtime origin instead of baked-in env var to avoid stale URLs after deployment
const API = typeof window !== 'undefined' ? window.location.origin : '';

// rrweb-recorder intercepts window.fetch and breaks .json()/.clone() in production
// (the same bug that hit the Clipping page). Use XHR for all Fireflies calls.
const xhr = (method, url, body) => new Promise((resolve, reject) => {
  try {
    const r = new XMLHttpRequest();
    r.open(method, url, true);
    if (body) r.setRequestHeader('Content-Type', 'application/json');
    r.onload = () => {
      try {
        let text = r.responseText || '';
        // Defensive JSON parsing - rrweb-recorder may corrupt response
        const start = text.indexOf('{');
        const end = text.lastIndexOf('}');
        if (start !== -1 && end !== -1 && end > start) {
          text = text.substring(start, end + 1);
        }
        // Remove control characters
        // eslint-disable-next-line no-control-regex
        text = text.replace(/[\x00-\x1F\x7F]/g, '').trim();
        const parsed = text ? JSON.parse(text) : {};
        if (r.status >= 200 && r.status < 300) resolve(parsed);
        else reject(new Error(parsed.detail || `HTTP ${r.status}`));
      } catch (e) {
        console.error('[xhr] Parse error:', e.message, 'Raw:', r.responseText?.substring(0, 100));
        if (r.status >= 200 && r.status < 300) {
          resolve({}); // Return empty on success with parse error
        } else {
          reject(e);
        }
      }
    };
    r.onerror = () => reject(new Error('Network error'));
    r.send(body ? JSON.stringify(body) : null);
  } catch (e) { reject(e); }
});

// XHR for FormData uploads (multipart)
// CRITICAL: rrweb-recorder corrupts XHR responses - we MUST handle this defensively
const xhrUpload = (url, formData, onProgress) => new Promise((resolve, reject) => {
  const r = new XMLHttpRequest();
  r.open('POST', url, true);
  
  if (onProgress) {
    r.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
  }
  
  r.onload = () => {
    const isSuccess = r.status >= 200 && r.status < 300;
    console.log('[xhrUpload] Status:', r.status, 'Success:', isSuccess);
    
    // For successful uploads, we don't even need to parse the response
    // The important thing is the HTTP status
    if (isSuccess) {
      // Try to parse for better feedback, but don't fail if we can't
      try {
        let text = r.responseText || '';
        // Skip any prefix junk and find JSON
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          console.log('[xhrUpload] Parsed response:', parsed);
          resolve(parsed);
        } else {
          console.log('[xhrUpload] No JSON found, returning success');
          resolve({ success: true, processed: 1, results: [], errors: [] });
        }
      } catch (parseErr) {
        console.warn('[xhrUpload] Parse error (ignoring):', parseErr.message);
        // Upload was successful even if we can't parse the response
        resolve({ success: true, processed: 1, results: [], errors: [], _parseError: true });
      }
    } else {
      // For errors, try to extract error message
      try {
        const text = r.responseText || '';
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          reject(new Error(parsed.detail || `Upload failed (HTTP ${r.status})`));
        } else {
          reject(new Error(`Upload failed (HTTP ${r.status})`));
        }
      } catch {
        reject(new Error(`Upload failed (HTTP ${r.status})`));
      }
    }
  };
  
  r.onerror = () => reject(new Error('Network error'));
  r.ontimeout = () => reject(new Error('Upload timed out'));
  r.send(formData);
});

const Transcriptions = () => {
  const { user } = useAuth();
  const userId = user?.id || user?._id;
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  const [showConnect, setShowConnect] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeDetail, setActiveDetail] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [manualAudioUrl, setManualAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [editingSpeakerId, setEditingSpeakerId] = useState(null);
  const [editingSpeakerName, setEditingSpeakerName] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [editingDate, setEditingDate] = useState(false);
  const [editDateValue, setEditDateValue] = useState('');
  const [editTimeValue, setEditTimeValue] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Folder State
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null); // null = show all
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [showAddToFolderMenu, setShowAddToFolderMenu] = useState(null); // transcript id or null
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');

  // Sorting State
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortMenuRef = useRef(null);

  // Sort options configuration
  const sortOptions = [
    { key: 'date-desc', label: 'Date Published (Newest)', sortBy: 'date', sortOrder: 'desc' },
    { key: 'date-asc', label: 'Date Published (Oldest)', sortBy: 'date', sortOrder: 'asc' },
    { key: 'created_at-desc', label: 'Date Added (Newest)', sortBy: 'created_at', sortOrder: 'desc' },
    { key: 'created_at-asc', label: 'Date Added (Oldest)', sortBy: 'created_at', sortOrder: 'asc' },
    { key: 'title-asc', label: 'Title (A-Z)', sortBy: 'title', sortOrder: 'asc' },
    { key: 'title-desc', label: 'Title (Z-A)', sortBy: 'title', sortOrder: 'desc' },
  ];

  const getCurrentSortLabel = () => {
    const current = sortOptions.find(opt => opt.sortBy === sortBy && opt.sortOrder === sortOrder);
    return current?.label || 'Sort';
  };

  // Close sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setShowSortMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close folder menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showAddToFolderMenu && !e.target.closest('[data-testid^="transcript-add-folder-"]') && !e.target.closest('[data-testid^="add-to-folder-"]')) {
        setShowAddToFolderMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddToFolderMenu]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reload when debounced search changes
  useEffect(() => {
    if (userId) {
      loadAll(1, sortBy, sortOrder, debouncedSearch);
    }
  }, [debouncedSearch]);

  // Bulk Upload State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState('new'); // 'new' or 'attach'
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Bulk Selection State
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkCategoryInput, setBulkCategoryInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // null = bulk, or transcript id for single

  // Toggle selection for a transcript
  const toggleSelection = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select all visible transcripts
  const selectAll = () => {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(it => it.id)));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkMode(false);
  };

  // Delete single transcript
  const handleDeleteTranscript = async (transcriptId) => {
    try {
      await xhr('DELETE', `${API}/api/fireflies/transcripts/${transcriptId}?user_id=${userId}`);
      toast.success('Transcript deleted');
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
    } catch (e) {
      toast.error(e.message || 'Failed to delete transcript');
    }
  };

  // Bulk delete transcripts
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      const ids = Array.from(selectedIds);
      let deleted = 0;
      
      for (const id of ids) {
        try {
          await xhr('DELETE', `${API}/api/fireflies/transcripts/${id}?user_id=${userId}`);
          deleted++;
        } catch (e) {
          console.error(`Failed to delete ${id}:`, e);
        }
      }
      
      toast.success(`Deleted ${deleted} transcript${deleted !== 1 ? 's' : ''}`);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      clearSelection();
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
    } catch (e) {
      toast.error(e.message || 'Failed to delete transcripts');
    }
  };

  // Bulk add category
  const handleBulkAddCategory = async () => {
    if (selectedIds.size === 0 || !bulkCategoryInput.trim()) return;
    
    try {
      const ids = Array.from(selectedIds);
      let updated = 0;
      
      for (const id of ids) {
        try {
          await xhr('POST', `${API}/api/fireflies/transcripts/${id}/categories?user_id=${userId}`, {
            category: bulkCategoryInput.trim()
          });
          updated++;
        } catch (e) {
          console.error(`Failed to add category to ${id}:`, e);
        }
      }
      
      toast.success(`Added category to ${updated} transcript${updated !== 1 ? 's' : ''}`);
      setBulkCategoryInput('');
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
    } catch (e) {
      toast.error(e.message || 'Failed to add category');
    }
  };

  // Bulk remove category
  const handleBulkRemoveCategory = async (categoryName) => {
    if (selectedIds.size === 0) return;
    
    try {
      const ids = Array.from(selectedIds);
      let updated = 0;
      
      for (const id of ids) {
        try {
          await xhr('DELETE', `${API}/api/fireflies/transcripts/${id}/categories/${encodeURIComponent(categoryName)}?user_id=${userId}`);
          updated++;
        } catch (e) {
          console.error(`Failed to remove category from ${id}:`, e);
        }
      }
      
      toast.success(`Removed category from ${updated} transcript${updated !== 1 ? 's' : ''}`);
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
    } catch (e) {
      toast.error(e.message || 'Failed to remove category');
    }
  };

  // Category Management State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null); // { oldName, newName }
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const categoryModalRef = useRef(null);

  // Close category modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryModalRef.current && !categoryModalRef.current.contains(e.target)) {
        setShowCategoryModal(false);
        setEditingCategory(null);
      }
    };
    if (showCategoryModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryModal]);

  // Add new category to a transcript (or multiple)
  const handleAddCategory = async (transcriptId, categoryName) => {
    if (!categoryName?.trim()) return;
    try {
      await xhr('POST', `${API}/api/fireflies/transcripts/${transcriptId}/categories?user_id=${userId}`, {
        category: categoryName.trim()
      });
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
      toast.success(`Added category "${categoryName.trim()}"`);
    } catch (e) {
      toast.error(e.message || 'Failed to add category');
    }
  };

  // Remove category from a transcript
  const handleRemoveCategory = async (transcriptId, categoryName) => {
    try {
      await xhr('DELETE', `${API}/api/fireflies/transcripts/${transcriptId}/categories/${encodeURIComponent(categoryName)}?user_id=${userId}`);
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
      toast.success(`Removed category "${categoryName}"`);
    } catch (e) {
      toast.error(e.message || 'Failed to remove category');
    }
  };

  // ============ Folder Functions ============
  // Uses localStorage as primary storage with API sync attempt
  // This ensures folders work even when MongoDB permissions are restricted
  
  const FOLDERS_STORAGE_KEY = `transcription_folders_${userId}`;
  
  // Get folders from localStorage
  const getLocalFolders = useCallback(() => {
    if (!userId) return [];
    try {
      const stored = localStorage.getItem(FOLDERS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, [userId, FOLDERS_STORAGE_KEY]);
  
  // Save folders to localStorage
  const saveLocalFolders = useCallback((foldersData) => {
    if (!userId) return;
    try {
      localStorage.setItem(FOLDERS_STORAGE_KEY, JSON.stringify(foldersData));
    } catch (e) {
      console.warn('Failed to save folders to localStorage:', e);
    }
  }, [userId, FOLDERS_STORAGE_KEY]);
  
  // Load folders - try API first, fall back to localStorage
  const loadFolders = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await xhr('GET', `${API}/api/conversations-transcripts/folders?user_id=${userId}`);
      const apiFolders = data.folders || [];
      // Merge with local folders (local takes precedence for new items)
      const localFolders = getLocalFolders();
      const mergedFolders = [...apiFolders];
      // Add any local-only folders that aren't in API
      localFolders.forEach(lf => {
        if (!mergedFolders.find(af => af.id === lf.id)) {
          mergedFolders.push(lf);
        }
      });
      setFolders(mergedFolders);
      saveLocalFolders(mergedFolders);
    } catch (e) {
      console.warn('API folder load failed, using localStorage:', e);
      setFolders(getLocalFolders());
    }
  }, [userId, getLocalFolders, saveLocalFolders]);

  // Create new folder - save locally, try API
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    
    const newFolder = {
      id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: newFolderName.trim(),
      color: 'bg-purple-500',
      user_id: userId,
      transcript_ids: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // Save locally first (guaranteed to work)
    const updatedFolders = [newFolder, ...folders];
    setFolders(updatedFolders);
    saveLocalFolders(updatedFolders);
    setNewFolderName('');
    setShowCreateFolder(false);
    toast.success(`Folder "${newFolder.name}" created`);
    
    // Try to sync to API (may fail due to permissions)
    try {
      const apiFolder = await xhr('POST', `${API}/api/conversations-transcripts/folders`, {
        name: newFolderName.trim(),
        user_id: userId
      });
      // Update with API id if successful
      const syncedFolders = updatedFolders.map(f => 
        f.id === newFolder.id ? { ...apiFolder } : f
      );
      setFolders(syncedFolders);
      saveLocalFolders(syncedFolders);
    } catch (e) {
      console.warn('API sync failed, folder saved locally only:', e);
    }
    
    setCreatingFolder(false);
  };

  // Delete folder
  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Transcripts inside will not be deleted.')) return;
    
    // Delete locally first
    const updatedFolders = folders.filter(f => f.id !== folderId);
    setFolders(updatedFolders);
    saveLocalFolders(updatedFolders);
    if (selectedFolder === folderId) setSelectedFolder(null);
    toast.success('Folder deleted');
    
    // Try API sync
    try {
      await xhr('DELETE', `${API}/api/conversations-transcripts/folders/${folderId}?user_id=${userId}`);
    } catch (e) {
      console.warn('API delete failed:', e);
    }
  };

  // Rename folder
  const handleRenameFolder = async (folderId) => {
    if (!editingFolderName.trim()) {
      setEditingFolder(null);
      return;
    }
    
    // Update locally first
    const updatedFolders = folders.map(f => 
      f.id === folderId ? { ...f, name: editingFolderName.trim(), updated_at: new Date().toISOString() } : f
    );
    setFolders(updatedFolders);
    saveLocalFolders(updatedFolders);
    setEditingFolder(null);
    toast.success('Folder renamed');
    
    // Try API sync
    try {
      await xhr('PATCH', `${API}/api/conversations-transcripts/folders/${folderId}?user_id=${userId}`, {
        name: editingFolderName.trim()
      });
    } catch (e) {
      console.warn('API rename failed:', e);
    }
  };

  // Add transcript to folder
  const handleAddToFolder = async (folderId, transcriptId) => {
    // Update locally first
    const updatedFolders = folders.map(f => {
      if (f.id === folderId) {
        const ids = f.transcript_ids || [];
        if (!ids.includes(transcriptId)) {
          return { ...f, transcript_ids: [...ids, transcriptId], updated_at: new Date().toISOString() };
        }
      }
      return f;
    });
    setFolders(updatedFolders);
    saveLocalFolders(updatedFolders);
    setShowAddToFolderMenu(null);
    toast.success('Added to folder');
    
    // Try API sync
    try {
      await xhr('POST', `${API}/api/conversations-transcripts/folders/${folderId}/add`, {
        user_id: userId,
        transcript_id: transcriptId
      });
    } catch (e) {
      console.warn('API add failed:', e);
    }
  };

  // Remove transcript from folder
  const handleRemoveFromFolder = async (folderId, transcriptId) => {
    // Update locally first
    const updatedFolders = folders.map(f => {
      if (f.id === folderId) {
        return { ...f, transcript_ids: (f.transcript_ids || []).filter(id => id !== transcriptId), updated_at: new Date().toISOString() };
      }
      return f;
    });
    setFolders(updatedFolders);
    saveLocalFolders(updatedFolders);
    toast.success('Removed from folder');
    
    // Try API sync
    try {
      await xhr('DELETE', `${API}/api/conversations-transcripts/folders/${folderId}/transcripts/${transcriptId}?user_id=${userId}`);
    } catch (e) {
      console.warn('API remove failed:', e);
    }
  };

  // Get folders containing a transcript
  const getFoldersForTranscript = (transcriptId) => {
    return folders.filter(f => (f.transcript_ids || []).includes(transcriptId));
  };

  // Load folders on mount
  useEffect(() => {
    if (userId) loadFolders();
  }, [userId, loadFolders]);

  // Rename a category across all transcripts
  const handleRenameCategory = async (oldName, newName) => {
    if (!newName?.trim() || newName.trim() === oldName) {
      setEditingCategory(null);
      return;
    }
    try {
      // Get all transcripts with this category
      const transcriptsWithCategory = items.filter(it => (it.categories || []).includes(oldName));
      
      // Update each transcript
      for (const transcript of transcriptsWithCategory) {
        // Remove old category
        await xhr('DELETE', `${API}/api/fireflies/transcripts/${transcript.id}/categories/${encodeURIComponent(oldName)}?user_id=${userId}`);
        // Add new category
        await xhr('POST', `${API}/api/fireflies/transcripts/${transcript.id}/categories?user_id=${userId}`, {
          category: newName.trim()
        });
      }
      
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
      toast.success(`Renamed "${oldName}" to "${newName.trim()}"`);
      setEditingCategory(null);
      if (activeFilter === oldName) {
        setActiveFilter(newName.trim());
      }
    } catch (e) {
      toast.error(e.message || 'Failed to rename category');
    }
  };

  // Delete a category from all transcripts
  const handleDeleteCategory = async (categoryName) => {
    try {
      const transcriptsWithCategory = items.filter(it => (it.categories || []).includes(categoryName));
      
      for (const transcript of transcriptsWithCategory) {
        await xhr('DELETE', `${API}/api/fireflies/transcripts/${transcript.id}/categories/${encodeURIComponent(categoryName)}?user_id=${userId}`);
      }
      
      await loadAll(currentPage, sortBy, sortOrder, debouncedSearch);
      toast.success(`Deleted category "${categoryName}"`);
      setCategoryToDelete(null);
      if (activeFilter === categoryName) {
        setActiveFilter('all');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to delete category');
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      return ['mp3', 'm4a', 'wav', 'webm', 'mpeg', 'mpga', 'mp4', 'pdf'].includes(ext);
    });
    
    if (validFiles.length === 0) {
      toast.error('Please drop MP3, WAV, M4A, or PDF files');
      return;
    }
    
    setUploadFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearUpload = () => {
    setUploadFiles([]);
    setUploadResults(null);
    setUploadProgress(0);
  };

  // Group files by stem to show which will be combined
  const getFileGroups = useCallback(() => {
    const groups = {};
    uploadFiles.forEach((file, idx) => {
      const stem = file.name.replace(/\.[^/.]+$/, '').toLowerCase();
      if (!groups[stem]) {
        groups[stem] = { audio: null, pdf: null, audioIdx: null, pdfIdx: null };
      }
      const ext = file.name.toLowerCase().split('.').pop();
      if (['mp3', 'm4a', 'wav', 'webm', 'mpeg', 'mpga', 'mp4'].includes(ext)) {
        groups[stem].audio = file;
        groups[stem].audioIdx = idx;
      } else if (ext === 'pdf') {
        groups[stem].pdf = file;
        groups[stem].pdfIdx = idx;
      }
    });
    return groups;
  }, [uploadFiles]);

  const fileGroups = getFileGroups();

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResults(null);

    let totalProcessed = 0;
    let allResults = [];
    let allErrors = [];
    
    try {
      // Upload files one at a time to avoid proxy timeouts
      // Group files by stem (matching names get uploaded together)
      const filesByGroup = {};
      for (const file of uploadFiles) {
        const stem = file.name.replace(/\.[^/.]+$/, '').toLowerCase();
        if (!filesByGroup[stem]) filesByGroup[stem] = [];
        filesByGroup[stem].push(file);
      }
      
      const groups = Object.values(filesByGroup);
      
      for (let i = 0; i < groups.length; i++) {
        const groupFiles = groups[i];
        const formData = new FormData();
        formData.append('user_id', userId);
        
        for (const file of groupFiles) {
          formData.append('files', file);
        }
        
        // Update progress based on group index
        const baseProgress = Math.round((i / groups.length) * 100);
        setUploadProgress(baseProgress);
        
        try {
          const result = await xhrUpload(
            `${API}/api/manual-transcripts/upload`,
            formData,
            (pct) => setUploadProgress(baseProgress + Math.round(pct / groups.length))
          );
          
          if (result.processed) totalProcessed += result.processed;
          if (result.results) allResults.push(...result.results);
          if (result.errors) allErrors.push(...result.errors);
        } catch (groupErr) {
          console.error(`Upload error for group ${i}:`, groupErr);
          allErrors.push({ filename: groupFiles.map(f => f.name).join(', '), error: groupErr.message });
        }
      }
      
      setUploadProgress(100);
      
      // Check if any results need polling for background processing
      const jobsNeedingPolling = allResults.filter(r => r.needs_polling);
      
      if (jobsNeedingPolling.length > 0) {
        toast.info(`🎙️ Transcribing ${jobsNeedingPolling.length} audio file(s) in background...`);
        
        // Poll for each job until complete
        const pollJob = async (jobId, title) => {
          const maxAttempts = 120; // 10 minutes max (5s intervals)
          let attempts = 0;
          
          while (attempts < maxAttempts) {
            await new Promise(r => setTimeout(r, 5000)); // Wait 5 seconds
            attempts++;
            
            try {
              const statusRes = await fetch(`${API}/api/manual-transcripts/job-status/${jobId}?user_id=${encodeURIComponent(userId)}`);
              const status = await statusRes.json();
              
              if (status.status === 'completed') {
                toast.success(`✅ "${title}" transcription complete!`);
                return true;
              } else if (status.status === 'error') {
                toast.error(`❌ "${title}" transcription failed: ${status.error || 'Unknown error'}`);
                return false;
              }
              // Still processing, continue polling
            } catch (pollErr) {
              console.error('Poll error:', pollErr);
              // Continue polling on network errors
            }
          }
          
          toast.warning(`⚠️ "${title}" transcription is taking longer than expected. Check back later.`);
          return false;
        };
        
        // Start polling all jobs in parallel
        Promise.all(
          jobsNeedingPolling.map(job => pollJob(job.id, job.title))
        ).then(() => {
          // Refresh the list after all jobs complete
          loadAll(1);
        });
      }
      
      setUploadResults({ 
        success: true, 
        processed: totalProcessed, 
        results: allResults, 
        errors: allErrors,
        backgroundJobs: jobsNeedingPolling.length
      });
      
      if (totalProcessed > 0) {
        if (jobsNeedingPolling.length > 0) {
          toast.success(`Uploaded ${totalProcessed} file(s). Transcriptions processing in background...`);
        } else {
          toast.success(`Successfully processed ${totalProcessed} transcript(s)`);
        }
      }
      
      if (allErrors.length > 0) {
        toast.error(`${allErrors.length} file(s) failed to process`);
      }
      
      // Refresh the list
      await new Promise(r => setTimeout(r, 500));
      await loadAll(1);
      
    } catch (err) {
      toast.error(err.message || 'Upload failed');
      setUploadResults({ success: false, error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (['mp3', 'm4a', 'wav', 'webm', 'mpeg', 'mpga', 'mp4'].includes(ext)) {
      return <Music size={16} className="text-purple-400" />;
    }
    return <FileText size={16} className="text-blue-400" />;
  };

  // Attach audio files to existing transcriptions
  const handleAttachAudio = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select audio files to attach');
      return;
    }
    
    // Filter to only audio files
    const audioFiles = uploadFiles.filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      return ['mp3', 'm4a', 'wav', 'webm', 'mpeg', 'mpga', 'mp4', 'ogg', 'flac'].includes(ext);
    });
    
    if (audioFiles.length === 0) {
      toast.error('No audio files selected. Please select MP3, WAV, or other audio files.');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      audioFiles.forEach(f => formData.append('files', f));
      
      const result = await xhrUpload(`${API}/api/manual-transcripts/attach-audio`, formData, (pct) => {
        setUploadProgress(pct);
      });
      
      setUploadResults(result);
      
      // Show detailed results
      if (result.summary) {
        const { matched, unmatched, errors } = result.summary;
        if (matched > 0) {
          toast.success(`✓ Attached audio to ${matched} existing transcription(s)`);
        }
        if (unmatched > 0) {
          toast.info(`Created ${unmatched} new transcription(s) for unmatched files`);
        }
        if (errors > 0) {
          toast.error(`${errors} file(s) failed to process`);
        }
      }
      
      // Refresh the list
      await new Promise(r => setTimeout(r, 500));
      await loadAll(1, sortBy, sortOrder, debouncedSearch);
      
    } catch (err) {
      toast.error(err.message || 'Failed to attach audio files');
      setUploadResults({ success: false, error: err.message });
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Aggregate every unique category across all transcripts → tab list
  const allCategories = React.useMemo(() => {
    const set = new Set();
    for (const it of items) {
      for (const c of (it.categories || [])) set.add(c);
    }
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = React.useMemo(() => {
    let result = items;
    
    // Filter by folder first
    if (selectedFolder) {
      const folder = folders.find(f => f.id === selectedFolder);
      if (folder) {
        const folderIds = folder.transcript_ids || [];
        result = result.filter(it => folderIds.includes(it.id) || folderIds.includes(it.fireflies_id));
      }
    }
    
    // Then filter by category
    if (activeFilter === 'all') return result;
    if (activeFilter === '__uncategorized__') {
      return result.filter(it => !(it.categories || []).length);
    }
    return result.filter(it => (it.categories || []).includes(activeFilter));
  }, [items, activeFilter, selectedFolder, folders]);

  const loadAll = async (page = 1, newSortBy = sortBy, newSortOrder = sortOrder, search = debouncedSearch) => {
    if (!userId) return;
    setLoading(true);
    try {
      const s = await xhr('GET', `${API}/api/fireflies/status?user_id=${userId}`);
      setStatus(s);
      
      // Fetch transcripts with pagination, sorting, and search (both Fireflies and manual)
      const skip = (page - 1) * ITEMS_PER_PAGE;
      let url = `${API}/api/fireflies/transcripts?user_id=${userId}&limit=${ITEMS_PER_PAGE}&skip=${skip}&sort_by=${newSortBy}&sort_order=${newSortOrder}`;
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      const list = await xhr('GET', url);
      setItems(list.items || []);
      setTotalItems(list.total || 0);
      setCurrentPage(page);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort change
  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setShowSortMenu(false);
    loadAll(1, newSortBy, newSortOrder, debouncedSearch); // Reset to first page when sorting changes
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Format date helper - handles both timestamp (ms) and ISO string
  // Shows actual recording/meeting date in format: "Wed, Aug 14, 2024 11:00 AM"
  const formatTranscriptDate = (date) => {
    if (!date) return '—';
    try {
      let d;
      if (typeof date === 'number') {
        // Fireflies returns timestamps - check if seconds or milliseconds
        // If the number is less than 10 billion, it's likely seconds (before year 2286)
        // If greater, it's milliseconds
        const timestamp = date < 10000000000 ? date * 1000 : date;
        d = new Date(timestamp);
      } else if (typeof date === 'string') {
        // ISO string or other date format
        d = new Date(date);
      } else {
        return '—';
      }
      
      if (isNaN(d.getTime())) return '—';
      
      // Check if date is reasonable (between 2000 and 2100)
      const year = d.getFullYear();
      if (year < 2000 || year > 2100) return '—';
      
      return d.toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '—';
    }
  };

  // Get the actual meeting date from transcript - uses sentences start_time if available
  const getMeetingDate = (transcript) => {
    if (!transcript) return null;
    
    // First, try to get date from first sentence's start_time (most accurate for Fireflies)
    const sentences = transcript.sentences || [];
    if (sentences.length > 0 && sentences[0].start_time) {
      const startTime = sentences[0].start_time;
      // Fireflies start_time is in seconds
      if (typeof startTime === 'number' && startTime > 0) {
        const timestamp = startTime < 10000000000 ? startTime * 1000 : startTime;
        const d = new Date(timestamp);
        if (!isNaN(d.getTime()) && d.getFullYear() >= 2000 && d.getFullYear() <= 2100) {
          return formatTranscriptDate(startTime);
        }
      }
    }
    
    // Fall back to the date field if sentences don't have valid start_time
    if (transcript.date) {
      const formatted = formatTranscriptDate(transcript.date);
      // Only return if it's a valid date (not the sync date which would be recent)
      if (formatted !== '—') {
        return formatted;
      }
    }
    
    return null;
  };

  // Get meeting duration from transcript - uses last sentence's end_time minus first's start_time
  const getMeetingDuration = (transcript) => {
    if (!transcript) return null;
    
    // Try to calculate from sentences
    const sentences = transcript.sentences || [];
    if (sentences.length > 0) {
      const firstSentence = sentences[0];
      const lastSentence = sentences[sentences.length - 1];
      
      if (firstSentence.start_time && lastSentence.end_time) {
        const duration = lastSentence.end_time - firstSentence.start_time;
        if (duration > 0 && duration < 86400) { // Reasonable duration (less than 24 hours)
          const hours = Math.floor(duration / 3600);
          const mins = Math.floor((duration % 3600) / 60);
          const secs = Math.floor(duration % 60);
          if (hours > 0) {
            return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
          }
          return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
      }
    }
    
    // Fall back to duration field
    if (transcript.duration) {
      return formatDuration(transcript.duration);
    }
    
    return null;
  };

  // Format duration helper - converts seconds to "MM:SS" format
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Page change handler
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadAll(newPage, sortBy, sortOrder, debouncedSearch);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => { loadAll(1); /* eslint-disable-next-line */ }, [userId]);

  // If we arrived with ?open=<transcript_id>, auto-open that transcript's detail modal.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || !userId || loading) return;
    openDetail(openId);
    // Clear the query param so back/forward feels clean.
    searchParams.delete('open');
    setSearchParams(searchParams, { replace: true });
  }, [loading, userId]);

  const handleConnect = async () => {
    if (!apiKey.trim()) return toast.error('Paste your Fireflies API key first');
    setConnecting(true);
    try {
      const d = await xhr('POST', `${API}/api/fireflies/connect`, {
        user_id: userId,
        api_key: apiKey.trim(),
      });
      toast.success(`Connected as ${d.fireflies_email}`);
      setApiKey('');
      setShowConnect(false);
      await loadAll();
      handleSync();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const d = await xhr('POST', `${API}/api/fireflies/sync?user_id=${userId}`);
      toast.success(d.synced ? `Synced ${d.synced} new transcript(s)` : 'Already up to date');
      await loadAll(1); // Go to first page after sync
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Fireflies? Cached transcripts will be removed.')) return;
    await xhr('POST', `${API}/api/fireflies/disconnect?user_id=${userId}`);
    toast.success('Disconnected');
    await loadAll(1);
  };

  const openDetail = async (id) => {
    setActiveDetail({ loading: true });
    setBreakdown(null);
    setManualAudioUrl(null);
    setBreakdownLoading(false);
    
    try {
      // First, fetch transcript details (fast) - show modal immediately
      const transcriptData = await xhr('GET', `${API}/api/fireflies/transcripts/${id}?user_id=${userId}`);
      setActiveDetail({ data: transcriptData });
      
      // Check if breakdown is already cached in the transcript data - instant display
      if (transcriptData.ai_breakdown && Object.keys(transcriptData.ai_breakdown).length > 0) {
        setBreakdown(transcriptData.ai_breakdown);
        // No loading needed - we have it!
      } else {
        // No cached breakdown - fetch/generate in background (may take a few seconds with Claude)
        setBreakdownLoading(true);
        xhr('POST', `${API}/api/fireflies/transcripts/${id}/breakdown?user_id=${userId}&regenerate=false`)
          .then(breakdownData => {
            if (breakdownData?.breakdown) {
              setBreakdown(breakdownData.breakdown);
            }
          })
          .catch(err => console.warn('Could not generate breakdown:', err))
          .finally(() => setBreakdownLoading(false));
      }
      
      // For manual uploads with audio, fetch the audio data (non-blocking)
      if (transcriptData.source === 'manual' && (transcriptData.audio_filename || transcriptData.has_audio)) {
        setLoadingAudio(true);
        xhr('GET', `${API}/api/manual-transcripts/audio/${id}?user_id=${userId}`)
          .then(audioResp => {
            if (audioResp.audio_url) {
              setManualAudioUrl(audioResp.audio_url);
            }
          })
          .catch(audioErr => console.warn('Could not load audio:', audioErr))
          .finally(() => setLoadingAudio(false));
      }
    } catch (e) {
      toast.error(e.message);
      setActiveDetail(null);
      setBreakdownLoading(false);
    }
  };

  const generateBreakdown = async (regenerate = false) => {
    if (!activeDetail?.data?.id) return;
    setBreakdownLoading(true);
    try {
      const d = await xhr(
        'POST',
        `${API}/api/fireflies/transcripts/${activeDetail.data.id}/breakdown?user_id=${userId}&regenerate=${regenerate}`,
      );
      setBreakdown(d.breakdown);
      toast.success(d.cached ? 'Loaded cached breakdown' : 'AI breakdown ready');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBreakdownLoading(false);
    }
  };

  // Save a renamed speaker
  const saveSpeakerName = async () => {
    if (!editingSpeakerId || !editingSpeakerName.trim()) { setEditingSpeakerId(null); return; }
    try {
      const d = await xhr(
        'PATCH',
        `${API}/api/fireflies/transcripts/${activeDetail.data.id}/speakers?user_id=${userId}`,
        { renames: { [String(editingSpeakerId)]: editingSpeakerName.trim() } },
      );
      setActiveDetail(prev => ({
        ...prev,
        data: {
          ...prev.data,
          speakers: d.speakers,
          sentences: (prev.data.sentences || []).map(s =>
            String(s.speaker_id) === String(editingSpeakerId)
              ? { ...s, speaker_name: editingSpeakerName.trim() }
              : s,
          ),
        },
      }));
      setItems(prev => prev.map(it => it.id === activeDetail.data.id ? { ...it, speakers: d.speakers } : it));
      toast.success('Speaker renamed');
    } catch (e) { toast.error(e.message); }
    finally { setEditingSpeakerId(null); setEditingSpeakerName(''); }
  };

  // Save edited title
  const handleSaveTitle = async () => {
    if (!editTitleValue.trim() || !activeDetail?.data?.id) {
      setEditingTitle(false);
      return;
    }
    try {
      await xhr(
        'PATCH',
        `${API}/api/fireflies/transcripts/${activeDetail.data.id}/title?user_id=${userId}`,
        { title: editTitleValue.trim() }
      );
      // Update local state
      setActiveDetail(prev => ({
        ...prev,
        data: { ...prev.data, title: editTitleValue.trim() }
      }));
      setItems(prev => prev.map(it => 
        it.id === activeDetail.data.id ? { ...it, title: editTitleValue.trim() } : it
      ));
      toast.success('Title updated');
    } catch (e) {
      toast.error(e.message || 'Failed to update title');
    } finally {
      setEditingTitle(false);
      setEditTitleValue('');
    }
  };

  // Save edited date
  const handleSaveDate = async () => {
    if (!editDateValue || !activeDetail?.data?.id) {
      setEditingDate(false);
      return;
    }
    try {
      // Combine date and time into a timestamp
      const dateTimeStr = editTimeValue ? `${editDateValue}T${editTimeValue}` : `${editDateValue}T00:00`;
      const timestamp = new Date(dateTimeStr).getTime();
      
      await xhr(
        'PATCH',
        `${API}/api/fireflies/transcripts/${activeDetail.data.id}/date?user_id=${userId}`,
        { date: timestamp }
      );
      // Update local state
      setActiveDetail(prev => ({
        ...prev,
        data: { ...prev.data, date: timestamp }
      }));
      setItems(prev => prev.map(it => 
        it.id === activeDetail.data.id ? { ...it, date: timestamp } : it
      ));
      toast.success('Date updated');
    } catch (e) {
      toast.error(e.message || 'Failed to update date');
    } finally {
      setEditingDate(false);
      setEditDateValue('');
      setEditTimeValue('');
    }
  };

  // Start editing date with current values
  const startEditingDate = () => {
    const transcript = activeDetail?.data;
    if (!transcript) return;
    
    // Get the current date from getMeetingDate logic
    let dateMs = null;
    if (transcript.sentences?.length > 0 && transcript.sentences[0].start_time) {
      const startTime = transcript.sentences[0].start_time;
      dateMs = startTime < 1e12 ? startTime * 1000 : startTime;
    } else if (transcript.date) {
      dateMs = transcript.date < 1e12 ? transcript.date * 1000 : transcript.date;
    }
    
    if (dateMs && !isNaN(dateMs)) {
      const d = new Date(dateMs);
      setEditDateValue(d.toISOString().split('T')[0]);
      setEditTimeValue(d.toTimeString().slice(0, 5));
    } else {
      // Default to today
      const now = new Date();
      setEditDateValue(now.toISOString().split('T')[0]);
      setEditTimeValue(now.toTimeString().slice(0, 5));
    }
    setEditingDate(true);
  };

  const addCategory = async () => {
    const cat = newCategory.trim();
    if (!cat || !activeDetail?.data?.id) return;
    try {
      const d = await xhr(
        'POST',
        `${API}/api/fireflies/transcripts/${activeDetail.data.id}/categories?user_id=${userId}`,
        { category: cat },
      );
      setActiveDetail(prev => ({ ...prev, data: { ...prev.data, categories: d.categories } }));
      setItems(prev => prev.map(it => it.id === activeDetail.data.id ? { ...it, categories: d.categories } : it));
      setNewCategory('');
    } catch (e) { toast.error(e.message); }
  };

  const removeCategory = async (cat) => {
    if (!activeDetail?.data?.id) return;
    try {
      const d = await xhr(
        'DELETE',
        `${API}/api/fireflies/transcripts/${activeDetail.data.id}/categories/${encodeURIComponent(cat)}?user_id=${userId}`,
      );
      setActiveDetail(prev => ({ ...prev, data: { ...prev.data, categories: d.categories } }));
      setItems(prev => prev.map(it => it.id === activeDetail.data.id ? { ...it, categories: d.categories } : it));
    } catch (e) { toast.error(e.message); }
  };

  const sendToMeetings = async (transcriptId, e) => {
    if (e) e.stopPropagation();
    try {
      const d = await xhr('POST', `${API}/api/fireflies/transcripts/${transcriptId}/send-to-meetings?user_id=${userId}`);
      toast.success(d.already_existed ? 'Already on your Meetings page' : 'Added to Meetings');
    } catch (err) { toast.error(err.message); }
  };


  return (
    <div className="min-h-screen bg-black text-white p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Mic size={26} className="text-white" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Transcriptions</h1>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Bulk Edit Toggle */}
            <button
              onClick={() => {
                setBulkMode(!bulkMode);
                if (bulkMode) clearSelection();
              }}
              data-testid="bulk-edit-toggle"
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors border ${
                bulkMode 
                  ? 'bg-purple-600 text-white border-purple-500' 
                  : 'bg-[#161A22] hover:bg-[#262C38] text-gray-300 border-white/10 hover:border-white/30'
              }`}
            >
              <CheckSquare size={14} />
              {bulkMode ? 'Exit Bulk Edit' : 'Bulk Edit'}
            </button>

            {/* Sort Dropdown */}
            <div className="relative" ref={sortMenuRef}>
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                data-testid="sort-dropdown-btn"
                className="inline-flex items-center gap-2 bg-[#161A22] hover:bg-[#262C38] text-gray-300 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors border border-white/10 hover:border-white/30"
              >
                <ArrowUpDown size={14} />
                <span className="hidden sm:inline">{getCurrentSortLabel()}</span>
                <span className="sm:hidden">Sort</span>
                <ChevronDown size={14} className={`transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showSortMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-[#0C0E13] border border-white/15 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="py-1">
                    {sortOptions.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => handleSortChange(opt.sortBy, opt.sortOrder)}
                        data-testid={`sort-option-${opt.key}`}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          sortBy === opt.sortBy && sortOrder === opt.sortOrder
                            ? 'bg-white/10 text-white'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Manual Upload Button - Always visible */}
            <button
              onClick={() => setShowUploadModal(true)}
              data-testid="manual-upload-btn"
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
            >
              <Upload size={16} /> Upload Files
            </button>
            
            {status.connected ? (
              <>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  data-testid="fireflies-sync-btn"
                  className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-lg font-medium hover:bg-gray-200 disabled:opacity-50"
                >
                  {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  {syncing ? 'Syncing…' : 'Sync now'}
                </button>
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center gap-2 bg-[#161A22] hover:bg-[#262C38] text-gray-300 px-3 py-2.5 rounded-lg text-sm"
                  title="Disconnect Fireflies"
                >
                  <Trash2 size={14} />
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowConnect(true)}
                data-testid="fireflies-connect-btn"
                className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-lg font-medium hover:bg-gray-200"
              >
                <Plug size={16} /> Connect Fireflies
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search transcriptions by title, content, or speaker..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="transcriptions-search-input"
              className="w-full bg-[#0C0E13] border border-white/15 rounded-xl py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-white/40 focus:shadow-[0_0_16px_rgba(255,255,255,0.1)] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                data-testid="clear-search-btn"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {debouncedSearch && (
            <p className="text-sm text-gray-500 mt-2">
              {loading ? 'Searching...' : `Found ${totalItems} result${totalItems !== 1 ? 's' : ''} for "${debouncedSearch}"`}
            </p>
          )}
        </div>

        {/* Status row */}
        {status.connected && (
          <div className="bg-black border border-white/30 rounded-xl p-4 mb-8 flex flex-wrap items-center gap-4 text-sm transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
            <div className="flex items-center gap-2 text-emerald-300">
              <CheckCircle2 size={16} /> Connected
            </div>
            <div className="text-gray-400">
              <span className="text-gray-500">Account:</span> {status.fireflies_email}
            </div>
            <div className="text-gray-400">
              <span className="text-gray-500">Last sync:</span>{' '}
              {status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : 'never'}
            </div>
            <div className="text-gray-400">
              <span className="text-gray-500">Cached:</span> {status.transcript_count || 0}
            </div>
          </div>
        )}

        {/* Folders and Category tab menu */}
        <div className="mb-6">
          {/* Folders Section - Always visible so users can create folders before having transcripts */}
            <div className="mb-4 pb-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Folder size={14} className="text-purple-400" />
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Folders</span>
                  <span className="text-xs text-gray-600">({folders.length})</span>
                </div>
                <button
                  onClick={() => setShowCreateFolder(true)}
                  className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                  data-testid="create-folder-btn"
                >
                  <FolderPlus size={12} /> New Folder
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {/* All Transcripts Button */}
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    selectedFolder === null
                      ? 'bg-purple-500/20 border-purple-400/50 text-purple-200'
                      : 'bg-black border-white/20 text-gray-300 hover:border-purple-400/30'
                  }`}
                  data-testid="folder-all"
                >
                  <FolderOpen size={12} /> All ({items.length})
                </button>
                
                {/* Folder Buttons */}
                {folders.map(folder => {
                  const count = (folder.transcript_ids || []).length;
                  const isEditing = editingFolder === folder.id;
                  
                  return (
                    <div key={folder.id} className="relative group">
                      {isEditing ? (
                        <div className="flex items-center gap-1 bg-black border border-purple-400/50 rounded-lg px-2 py-1">
                          <input
                            value={editingFolderName}
                            onChange={(e) => setEditingFolderName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameFolder(folder.id);
                              if (e.key === 'Escape') setEditingFolder(null);
                            }}
                            className="bg-transparent text-xs text-white w-20 focus:outline-none"
                            autoFocus
                          />
                          <button onClick={() => handleRenameFolder(folder.id)} className="text-green-400 hover:text-green-300">
                            <Check size={12} />
                          </button>
                          <button onClick={() => setEditingFolder(null)} className="text-gray-400 hover:text-white">
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedFolder(folder.id)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                            selectedFolder === folder.id
                              ? 'bg-purple-500/20 border-purple-400/50 text-purple-200'
                              : 'bg-black border-white/20 text-gray-300 hover:border-purple-400/30'
                          }`}
                          data-testid={`folder-${folder.id}`}
                        >
                          <Folder size={12} className="text-purple-400" />
                          {folder.name} ({count})
                        </button>
                      )}
                      
                      {/* Folder Actions (on hover when selected) */}
                      {!isEditing && selectedFolder === folder.id && (
                        <div className="absolute -right-1 -top-1 flex gap-0.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingFolder(folder.id);
                              setEditingFolderName(folder.name);
                            }}
                            className="p-1 bg-black border border-white/20 rounded text-gray-400 hover:text-white text-[10px]"
                            title="Rename"
                          >
                            <Edit2 size={10} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder.id);
                            }}
                            className="p-1 bg-black border border-white/20 rounded text-gray-400 hover:text-red-400 text-[10px]"
                            title="Delete"
                          >
                            <Trash2 size={10} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Create Folder Inline Form */}
              {showCreateFolder && (
                <div className="mt-3 flex items-center gap-2 bg-black border border-purple-400/30 rounded-lg p-2">
                  <FolderPlus size={16} className="text-purple-400" />
                  <input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateFolder();
                      if (e.key === 'Escape') { setShowCreateFolder(false); setNewFolderName(''); }
                    }}
                    placeholder="Folder name..."
                    className="flex-1 bg-transparent text-sm text-white placeholder:text-gray-500 focus:outline-none"
                    autoFocus
                    data-testid="new-folder-input"
                  />
                  <button
                    onClick={handleCreateFolder}
                    disabled={!newFolderName.trim() || creatingFolder}
                    className="px-3 py-1 bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-white text-xs rounded transition-colors"
                  >
                    {creatingFolder ? <Loader2 size={12} className="animate-spin" /> : 'Create'}
                  </button>
                  <button
                    onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}
                    className="p-1 text-gray-400 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
            
            {/* Categories Section - Only show when there are items */}
            {items.length > 0 && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <Tag size={14} className="text-gray-500" />
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">Categories</span>
                  <button
                    onClick={() => setShowCategoryModal(true)}
                    className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors px-2 py-1 rounded hover:bg-white/10"
                    data-testid="manage-categories-btn"
                  >
                    <Settings size={12} /> Manage
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 overflow-x-auto pb-1">
                  <button
                    onClick={() => setActiveFilter('all')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                      activeFilter === 'all'
                        ? 'bg-white text-black border-white'
                        : 'bg-black border-white/20 text-gray-300 hover:border-white/50 hover:shadow-[0_0_14px_rgba(255,255,255,0.12)]'
                    }`}
                    data-testid="tab-all"
                  >
                    All ({items.length})
                  </button>
                  {allCategories.map(cat => {
                    const count = items.filter(it => (it.categories || []).includes(cat)).length;
                    return (
                      <button
                        key={cat}
                        onClick={() => setActiveFilter(cat)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                          activeFilter === cat
                            ? 'bg-white text-black border-white'
                            : 'bg-black border-white/20 text-gray-300 hover:border-white/50 hover:shadow-[0_0_14px_rgba(255,255,255,0.12)]'
                        }`}
                        data-testid={`tab-${cat}`}
                      >
                        {cat} ({count})
                      </button>
                    );
                  })}
                  {items.some(it => !(it.categories || []).length) && (
                    <button
                      onClick={() => setActiveFilter('__uncategorized__')}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                        activeFilter === '__uncategorized__'
                          ? 'bg-white text-black border-white'
                          : 'bg-black border-white/20 text-gray-300 hover:border-white/50 hover:shadow-[0_0_14px_rgba(255,255,255,0.12)]'
                      }`}
                      data-testid="tab-uncategorized"
                    >
                      Uncategorized ({items.filter(it => !(it.categories || []).length).length})
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

        {/* Category Management Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div 
              ref={categoryModalRef}
              className="bg-[#0C0E13] border border-white/20 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden"
            >
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Manage Categories</h3>
                <button 
                  onClick={() => { setShowCategoryModal(false); setEditingCategory(null); }}
                  className="text-gray-500 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {/* Add New Category */}
                <div className="mb-6">
                  <label className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2 block">
                    Add New Category
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Category name..."
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newCategoryName.trim()) {
                          // Will be added to selected transcript in detail view
                          toast.info('Select a transcript and add this category from the detail view');
                          setNewCategoryName('');
                        }
                      }}
                      className="flex-1 bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/50"
                      data-testid="new-category-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Open a transcript to add categories to it
                  </p>
                </div>

                {/* Existing Categories */}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2 block">
                    Existing Categories ({allCategories.length})
                  </label>
                  {allCategories.length === 0 ? (
                    <p className="text-sm text-gray-500">No categories yet. Add categories from transcript details.</p>
                  ) : (
                    <div className="space-y-2">
                      {allCategories.map(cat => {
                        const count = items.filter(it => (it.categories || []).includes(cat)).length;
                        const isEditing = editingCategory?.oldName === cat;
                        
                        return (
                          <div 
                            key={cat}
                            className="flex items-center gap-2 bg-black/50 border border-white/10 rounded-lg px-3 py-2"
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                value={editingCategory.newName}
                                onChange={(e) => setEditingCategory({ ...editingCategory, newName: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameCategory(cat, editingCategory.newName);
                                  if (e.key === 'Escape') setEditingCategory(null);
                                }}
                                autoFocus
                                className="flex-1 bg-transparent border-none text-sm focus:outline-none"
                                data-testid={`edit-category-input-${cat}`}
                              />
                            ) : (
                              <span className="flex-1 text-sm">{cat}</span>
                            )}
                            <span className="text-xs text-gray-500 px-2">{count} items</span>
                            
                            {isEditing ? (
                              <>
                                <button
                                  onClick={() => handleRenameCategory(cat, editingCategory.newName)}
                                  className="text-emerald-400 hover:text-emerald-300 p-1"
                                  title="Save"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={() => setEditingCategory(null)}
                                  className="text-gray-500 hover:text-white p-1"
                                  title="Cancel"
                                >
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => setEditingCategory({ oldName: cat, newName: cat })}
                                  className="text-gray-500 hover:text-white p-1"
                                  title="Rename"
                                  data-testid={`rename-category-${cat}`}
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => setCategoryToDelete(cat)}
                                  className="text-gray-500 hover:text-red-400 p-1"
                                  title="Delete"
                                  data-testid={`delete-category-${cat}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Delete Confirmation */}
                {categoryToDelete && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-300 mb-3">
                      Delete category &ldquo;{categoryToDelete}&rdquo;? This will remove it from all {items.filter(it => (it.categories || []).includes(categoryToDelete)).length} transcripts.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDeleteCategory(categoryToDelete)}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm rounded-lg transition-colors"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setCategoryToDelete(null)}
                        className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="bg-black border border-white/30 rounded-xl p-12 text-center text-gray-400 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
            <Loader2 size={28} className="mx-auto animate-spin" />
          </div>
        ) : !status.connected && items.length === 0 ? (
          <div className="bg-black border border-white/30 rounded-xl p-12 text-center transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
            <Mic size={40} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No transcripts yet</h3>
            <p className="text-sm text-gray-400 max-w-md mx-auto mb-6">
              Upload MP3 and PDF files to transcribe, or connect Fireflies.ai to sync meeting transcripts automatically.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-5 py-2.5 rounded-lg font-medium transition-colors"
                data-testid="empty-upload-btn"
              >
                <Upload size={16} /> Upload Files
              </button>
              <button
                onClick={() => setShowConnect(true)}
                className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-lg font-medium hover:bg-gray-200"
              >
                <Plug size={16} /> Connect Fireflies
              </button>
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-black border border-white/30 rounded-xl p-12 text-center text-sm text-gray-400 transition-all hover:border-white/60 hover:shadow-[0_0_24px_rgba(255,255,255,0.18)]">
            {activeFilter === 'all'
              ? 'No transcripts yet. Upload files or sync from Fireflies.'
              : `No transcripts in "${activeFilter === '__uncategorized__' ? 'Uncategorized' : activeFilter}". Pick another category above.`}
          </div>
        ) : (
          <>
            {/* Bulk Actions Bar */}
            {bulkMode && (
              <div className="bg-[#0C0E13] border border-white/20 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-4">
                <button
                  onClick={selectAll}
                  className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
                  data-testid="select-all-btn"
                >
                  {selectedIds.size === filteredItems.length ? (
                    <CheckSquare size={16} className="text-purple-400" />
                  ) : (
                    <Square size={16} />
                  )}
                  {selectedIds.size === filteredItems.length ? 'Deselect All' : 'Select All'}
                </button>
                
                <span className="text-sm text-gray-500">
                  {selectedIds.size} selected
                </span>

                {selectedIds.size > 0 && (
                  <>
                    <div className="h-4 w-px bg-white/20" />
                    
                    {/* Add Category Input */}
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Add category..."
                        value={bulkCategoryInput}
                        onChange={(e) => setBulkCategoryInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleBulkAddCategory();
                        }}
                        className="bg-black border border-white/20 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-white/50 w-40"
                        data-testid="bulk-category-input"
                      />
                      <button
                        onClick={handleBulkAddCategory}
                        disabled={!bulkCategoryInput.trim()}
                        className="inline-flex items-center gap-1 text-sm text-emerald-400 hover:text-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus size={14} /> Add
                      </button>
                    </div>

                    <div className="h-4 w-px bg-white/20" />

                    {/* Bulk Delete */}
                    <button
                      onClick={() => { setDeleteTarget(null); setShowDeleteConfirm(true); }}
                      className="inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors"
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 size={14} /> Delete Selected
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredItems.map(it => (
                <div
                  key={it.id}
                  className={`group relative bg-black border hover:border-purple-500/50 rounded-xl p-4 transition-all hover:shadow-lg hover:shadow-purple-500/10 flex flex-col cursor-pointer ${
                    selectedIds.has(it.id) 
                      ? 'border-purple-500 shadow-[0_0_16px_rgba(168,85,247,0.3)]' 
                      : 'border-gray-700'
                  }`}
                  onClick={() => !bulkMode && openDetail(it.id)}
                  data-testid={`transcript-item-${it.id}`}
                >
                  {/* Delete button - appears on hover */}
                  {!bulkMode && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(it.id); setShowDeleteConfirm(true); }}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/80 border border-transparent opacity-0 group-hover:opacity-100 hover:bg-red-500/20 hover:border-red-500/30 text-gray-400 hover:text-red-400 transition-all z-10"
                      title="Delete"
                      data-testid={`transcript-delete-${it.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  
                  {/* Header: title + source badge */}
                  <div className="flex items-start gap-3 mb-3">
                    {/* Checkbox for bulk mode */}
                    {bulkMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelection(it.id); }}
                        className="flex-shrink-0 mt-1"
                        data-testid={`checkbox-${it.id}`}
                      >
                        {selectedIds.has(it.id) ? (
                          <CheckSquare size={20} className="text-purple-400" />
                        ) : (
                          <Square size={20} className="text-gray-500 hover:text-white" />
                        )}
                      </button>
                    )}
                    
                    <div className="min-w-0 flex-1">
                      <div className="text-white font-medium truncate" title={it.title}>
                        {it.title || 'Untitled Transcription'}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {getMeetingDate(it) || '—'}
                        {(() => {
                          const dur = getMeetingDuration(it);
                          return dur ? <span className="ml-2">· {dur}</span> : null;
                        })()}
                      </div>
                      {/* Speakers row */}
                      {(() => {
                        // Get speakers from multiple sources
                        const speakerNames = [];
                        // From speakers array
                        if (it.speakers?.length > 0) {
                          it.speakers.forEach(s => {
                            const name = typeof s === 'string' ? s : (s.name || s.displayName);
                            if (name && !speakerNames.includes(name)) speakerNames.push(name);
                          });
                        }
                        // From participants array
                        if (it.participants?.length > 0) {
                          it.participants.forEach(p => {
                            const name = typeof p === 'string' ? p : (p.name || p.displayName);
                            if (name && !speakerNames.includes(name)) speakerNames.push(name);
                          });
                        }
                        // From sentences (speaker_name field)
                        if (it.sentences?.length > 0) {
                          it.sentences.forEach(s => {
                            if (s.speaker_name && !speakerNames.includes(s.speaker_name)) {
                              speakerNames.push(s.speaker_name);
                            }
                          });
                        }
                        // From AI breakdown speaker_contributions
                        if (it.ai_breakdown?.speaker_contributions?.length > 0) {
                          it.ai_breakdown.speaker_contributions.forEach(sc => {
                            if (sc.name && !speakerNames.includes(sc.name)) speakerNames.push(sc.name);
                          });
                        }
                        // From host_email or organizer_email as fallback
                        if (speakerNames.length === 0) {
                          if (it.host_email) speakerNames.push(it.host_email.split('@')[0]);
                          else if (it.organizer_email) speakerNames.push(it.organizer_email.split('@')[0]);
                        }
                        // Filter out generic names
                        const filteredSpeakers = speakerNames.filter(n => 
                          n && !n.toLowerCase().includes('speaker ') && n !== 'Manual Upload'
                        );
                        if (filteredSpeakers.length === 0) return null;
                        const displaySpeakers = filteredSpeakers.slice(0, 3);
                        const remaining = filteredSpeakers.length - 3;
                        return (
                          <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-500">
                            <Users size={10} className="flex-shrink-0" />
                            <span className="truncate">
                              {displaySpeakers.join(', ')}
                              {remaining > 0 && <span className="text-gray-600"> +{remaining}</span>}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* AI Overview or Summary */}
                  {it.overview ? (
                    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-2.5 mb-3 text-sm">
                      <p className="text-gray-300 text-xs line-clamp-3">{it.overview}</p>
                    </div>
                  ) : it.ai_breakdown?.overview ? (
                    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-2.5 mb-3 text-sm">
                      <p className="text-gray-300 text-xs line-clamp-3">{it.ai_breakdown.overview}</p>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 italic mb-3">No summary available</div>
                  )}
                  
                  {/* Meta badges row */}
                  <div className="flex flex-wrap gap-1.5 mb-3 mt-auto">
                    {(it.audio_filename || it.has_audio || it.audio_url) && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-400/30 text-purple-200">
                        <Music size={9} /> Audio
                      </span>
                    )}
                    {it.ai_breakdown && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200">
                        <Sparkles size={9} /> AI
                      </span>
                    )}
                    {it.host_email && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-gray-500/15 border border-gray-400/30 text-gray-300 truncate max-w-[120px]">
                        {it.host_email}
                      </span>
                    )}
                  </div>
                  
                  {/* Folder badges row - only show if in folders */}
                  {getFoldersForTranscript(it.id).length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap pt-2 border-t border-gray-800">
                      {getFoldersForTranscript(it.id).map(f => (
                        <span
                          key={f.id}
                          className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30"
                          title={`In folder: ${f.name}`}
                        >
                          <Folder size={8} />
                          <span className="truncate max-w-[60px]">{f.name}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveFromFolder(f.id, it.id); }}
                            className="hover:text-red-400 ml-0.5"
                            title="Remove from folder"
                          >
                            <X size={8} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Add to Folder button - appears on hover */}
                  {!bulkMode && folders.length > 0 && (
                    <div className="relative pt-2">
                      <button
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setShowAddToFolderMenu(showAddToFolderMenu === it.id ? null : it.id); 
                        }}
                        className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg transition-colors ${
                          showAddToFolderMenu === it.id 
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                            : 'text-gray-500 hover:text-purple-300 hover:bg-purple-500/10'
                        }`}
                        data-testid={`transcript-add-folder-${it.id}`}
                      >
                        <FolderPlus size={12} />
                        <span>Add to folder</span>
                      </button>
                      
                      {showAddToFolderMenu === it.id && (
                        <div 
                          className="absolute left-0 bottom-full mb-1 w-48 bg-[#0C0E13] border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2 border-b border-white/10">
                            <span className="text-xs text-gray-500 uppercase tracking-wider">Add to folder</span>
                          </div>
                          <div className="max-h-40 overflow-y-auto">
                            {folders.map(folder => {
                              const isInFolder = (folder.transcript_ids || []).includes(it.id);
                              return (
                                <button
                                  key={folder.id}
                                  onClick={() => {
                                    if (isInFolder) {
                                      handleRemoveFromFolder(folder.id, it.id);
                                    } else {
                                      handleAddToFolder(folder.id, it.id);
                                    }
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                                    isInFolder 
                                      ? 'bg-purple-500/10 text-purple-300' 
                                      : 'text-gray-300 hover:bg-white/5'
                                  }`}
                                  data-testid={`add-to-folder-${folder.id}`}
                                >
                                  {isInFolder ? (
                                    <CheckCircle2 size={14} className="text-purple-400 flex-shrink-0" />
                                  ) : (
                                    <Folder size={14} className="text-gray-500 flex-shrink-0" />
                                  )}
                                  <span className="truncate">{folder.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#0C0E13] border border-white/20 rounded-2xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Delete</h3>
              <p className="text-sm text-gray-400 mb-6">
                {deleteTarget 
                  ? 'Are you sure you want to delete this transcript? This action cannot be undone.'
                  : `Are you sure you want to delete ${selectedIds.size} selected transcript${selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`
                }
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteTarget ? handleDeleteTranscript(deleteTarget) : handleBulkDelete()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm transition-colors"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && !loading && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="inline-flex items-center gap-1 px-3 py-2 bg-[#161A22] border border-white/20 rounded-lg text-sm text-gray-300 hover:border-white/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="pagination-prev"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            
            <div className="flex items-center gap-1">
              {/* First page */}
              {currentPage > 2 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 bg-[#161A22] border border-white/20 rounded-lg text-sm text-gray-300 hover:border-white/50 hover:text-white transition-all"
                  >
                    1
                  </button>
                  {currentPage > 3 && <span className="px-2 text-gray-500">...</span>}
                </>
              )}
              
              {/* Previous page */}
              {currentPage > 1 && (
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="px-3 py-2 bg-[#161A22] border border-white/20 rounded-lg text-sm text-gray-300 hover:border-white/50 hover:text-white transition-all"
                >
                  {currentPage - 1}
                </button>
              )}
              
              {/* Current page */}
              <button
                className="px-3 py-2 bg-white text-black border border-white rounded-lg text-sm font-medium"
                data-testid="pagination-current"
              >
                {currentPage}
              </button>
              
              {/* Next page */}
              {currentPage < totalPages && (
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="px-3 py-2 bg-[#161A22] border border-white/20 rounded-lg text-sm text-gray-300 hover:border-white/50 hover:text-white transition-all"
                >
                  {currentPage + 1}
                </button>
              )}
              
              {/* Last page */}
              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && <span className="px-2 text-gray-500">...</span>}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-2 bg-[#161A22] border border-white/20 rounded-lg text-sm text-gray-300 hover:border-white/50 hover:text-white transition-all"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="inline-flex items-center gap-1 px-3 py-2 bg-[#161A22] border border-white/20 rounded-lg text-sm text-gray-300 hover:border-white/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="pagination-next"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Page info */}
        {totalItems > 0 && !loading && (
          <div className="mt-4 text-center text-xs text-gray-500">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} transcripts
          </div>
        )}
      </div>

      {/* Connect modal */}
      {showConnect && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowConnect(false)}>
          <div className="bg-[#0C0E13] border border-white/15 rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Connect Fireflies.ai</h2>
              <button onClick={() => setShowConnect(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <ol className="text-xs text-gray-400 space-y-1 mb-4 list-decimal list-inside">
              <li>Open <a className="underline" href="https://app.fireflies.ai/integrations/fireflies-api" target="_blank" rel="noreferrer">Fireflies → Integrations → Fireflies API</a></li>
              <li>Copy your API key (requires Pro plan or above)</li>
              <li>Paste it below — OPERATR will verify it</li>
            </ol>
            <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Fireflies API Key</label>
            <input
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="ff-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="w-full px-3 py-2.5 mb-4 bg-black border border-white/20 rounded-lg text-sm placeholder-gray-600 focus:outline-none focus:border-white font-mono"
              data-testid="fireflies-apikey-input"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConnect(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="inline-flex items-center gap-2 px-5 py-2 bg-white text-black rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="fireflies-connect-submit"
              >
                {connecting ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />}
                {connecting ? 'Validating…' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {activeDetail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setActiveDetail(null)}>
          <div className="bg-[#0C0E13] border border-white/15 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {activeDetail.loading ? (
              <div className="p-12 text-center text-gray-400"><Loader2 size={24} className="mx-auto animate-spin mb-2" /> Loading…</div>
            ) : (
              <div>
                <div className="sticky top-0 bg-[#0C0E13] border-b border-white/10 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      {/* Editable Title */}
                      {editingTitle ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editTitleValue}
                            onChange={(e) => setEditTitleValue(e.target.value)}
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter') {
                                await handleSaveTitle();
                              } else if (e.key === 'Escape') {
                                setEditingTitle(false);
                                setEditTitleValue('');
                              }
                            }}
                            className="flex-1 bg-[#161A22] border border-white/20 rounded-lg px-3 py-1.5 text-lg font-semibold focus:outline-none focus:border-purple-500"
                            autoFocus
                            data-testid="edit-title-input"
                          />
                          <button
                            onClick={handleSaveTitle}
                            className="p-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-300"
                            title="Save"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => { setEditingTitle(false); setEditTitleValue(''); }}
                            className="p-1.5 hover:bg-white/10 border border-white/10 rounded-lg text-gray-400"
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <h3 className="text-lg font-semibold truncate">{activeDetail.data.title}</h3>
                          <button
                            onClick={() => {
                              setEditTitleValue(activeDetail.data.title || '');
                              setEditingTitle(true);
                            }}
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-all"
                            title="Edit title"
                            data-testid="edit-title-btn"
                          >
                            <Pencil size={14} />
                          </button>
                        </div>
                      )}
                      
                      {/* Editable Date/Time */}
                      {editingDate ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar size={12} className="text-gray-500 flex-shrink-0" />
                          <input
                            type="date"
                            value={editDateValue}
                            onChange={(e) => setEditDateValue(e.target.value)}
                            className="bg-[#161A22] border border-white/20 rounded px-2 py-0.5 text-xs focus:outline-none focus:border-purple-500"
                            data-testid="edit-date-input"
                          />
                          <input
                            type="time"
                            value={editTimeValue}
                            onChange={(e) => setEditTimeValue(e.target.value)}
                            className="bg-[#161A22] border border-white/20 rounded px-2 py-0.5 text-xs focus:outline-none focus:border-purple-500"
                            data-testid="edit-time-input"
                          />
                          <button
                            onClick={handleSaveDate}
                            className="p-1 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded text-purple-300"
                            title="Save"
                          >
                            <Check size={12} />
                          </button>
                          <button
                            onClick={() => { setEditingDate(false); setEditDateValue(''); setEditTimeValue(''); }}
                            className="p-1 hover:bg-white/10 border border-white/10 rounded text-gray-400"
                            title="Cancel"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 mt-1 group/date">
                          <p className="text-xs text-gray-500 flex flex-wrap items-center gap-x-2">
                            <span>
                              {getMeetingDate(activeDetail.data) || '—'}
                              {(() => {
                                const dur = getMeetingDuration(activeDetail.data);
                                return dur ? ` · ${dur}` : '';
                              })()}
                            </span>
                            {breakdown?.generated_title && (
                              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-400/30 text-purple-200">
                                <Sparkles size={10} />
                                {breakdown.generated_title}
                              </span>
                            )}
                          </p>
                          <button
                            onClick={startEditingDate}
                            className="p-1 opacity-0 group-hover/date:opacity-100 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-all"
                            title="Edit date & time"
                            data-testid="edit-date-btn"
                          >
                            <Pencil size={10} />
                          </button>
                        </div>
                      )}
                      
                      {/* Participants/Speakers */}
                      {(activeDetail.data.speakers?.length > 0 || activeDetail.data.participants?.length > 0) && (
                        <div className="flex items-center gap-2 mt-2">
                          <Users size={12} className="text-gray-500 flex-shrink-0" />
                          <div className="flex flex-wrap gap-1.5">
                            {(activeDetail.data.speakers || activeDetail.data.participants || []).map((speaker, idx) => {
                              const name = typeof speaker === 'string' ? speaker : (speaker.name || speaker.displayName || `Speaker ${idx + 1}`);
                              return (
                                <span 
                                  key={idx}
                                  className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300"
                                >
                                  {name}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {activeDetail.data.audio_url && (
                        <audio
                          controls
                          src={activeDetail.data.audio_url}
                          className="mt-3 w-full max-w-md"
                          preload="metadata"
                        >
                          Your browser does not support the audio element.
                        </audio>
                      )}
                      {/* Manual upload audio player */}
                      {!activeDetail.data.audio_url && (activeDetail.data.audio_filename || activeDetail.data.has_audio) && (
                        <div className="mt-3">
                          {loadingAudio ? (
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Loader2 size={14} className="animate-spin" />
                              Loading audio...
                            </div>
                          ) : manualAudioUrl ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <Music size={12} className="text-purple-400" />
                                {activeDetail.data.audio_filename}
                              </div>
                              <audio
                                controls
                                src={manualAudioUrl}
                                className="w-full max-w-md"
                                preload="metadata"
                              >
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Music size={14} className="text-purple-400" />
                              Audio file attached: {activeDetail.data.audio_filename}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Upload Audio button - show if no audio attached */}
                      {!activeDetail.data.audio_url && !activeDetail.data.audio_filename && !activeDetail.data.has_audio && (
                        <div className="mt-3">
                          <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#161A22] hover:bg-[#1E232D] border border-white/10 hover:border-white/20 rounded-lg text-xs text-gray-300 hover:text-white cursor-pointer transition-colors">
                            <Upload size={14} />
                            <span>Attach Audio</span>
                            <input
                              type="file"
                              accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('transcript_id', activeDetail.data.id);
                                formData.append('user_id', userId);
                                
                                try {
                                  toast.loading('Uploading audio...', { id: 'audio-upload' });
                                  const result = await xhrUpload(
                                    `${API}/api/manual-transcripts/attach-audio-single`,
                                    formData,
                                    () => {}
                                  );
                                  toast.success('Audio attached successfully', { id: 'audio-upload' });
                                  
                                  // Refresh the detail view
                                  const updated = await xhr('GET', `${API}/api/fireflies/transcripts/${activeDetail.data.id}?user_id=${userId}`);
                                  setActiveDetail({ data: updated });
                                  
                                  // Load the audio
                                  if (updated.has_audio || updated.audio_filename) {
                                    setLoadingAudio(true);
                                    try {
                                      const audioResp = await xhr('GET', `${API}/api/manual-transcripts/audio/${activeDetail.data.id}?user_id=${userId}`);
                                      if (audioResp.audio_url) {
                                        setManualAudioUrl(audioResp.audio_url);
                                      }
                                    } catch (audioErr) {
                                      console.warn('Could not load audio:', audioErr);
                                    } finally {
                                      setLoadingAudio(false);
                                    }
                                  }
                                } catch (err) {
                                  toast.error(err.message || 'Failed to attach audio', { id: 'audio-upload' });
                                }
                                
                                // Reset the input
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => generateBreakdown(!!breakdown)}
                        disabled={breakdownLoading}
                        className="inline-flex items-center gap-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                        data-testid="fireflies-breakdown-btn"
                        title={breakdown ? 'Regenerate AI breakdown' : 'Generate AI breakdown'}
                      >
                        {breakdownLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        {breakdownLoading ? 'Analyzing…' : breakdown ? 'Regenerate' : 'AI Breakdown'}
                      </button>
                      <button onClick={() => setActiveDetail(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
                    </div>
                  </div>

                  {/* Quick Actions - Categories & Send to Meetings */}
                  <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center gap-3">
                    {/* Send to Meetings Button */}
                    <button
                      onClick={() => sendToMeetings(activeDetail.data.id)}
                      className="inline-flex items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium"
                      data-testid="transcript-detail-add-meeting"
                    >
                      <Calendar size={14} /> Send to Meetings
                    </button>

                    {/* Existing Categories */}
                    {(activeDetail.data.categories || []).map(cat => (
                      <span key={cat} className="inline-flex items-center gap-1.5 bg-[#161A22] border border-white/15 rounded-full pl-2.5 pr-1 py-1 text-xs text-gray-200">
                        {cat}
                        <button onClick={() => removeCategory(cat)} className="p-0.5 text-gray-500 hover:text-red-400" title="Remove category">
                          <X size={10} />
                        </button>
                      </span>
                    ))}

                    {/* Add Category Input */}
                    <div className="inline-flex items-center bg-[#161A22] border border-dashed border-white/15 rounded-full">
                      <input
                        value={newCategory}
                        onChange={e => setNewCategory(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addCategory(); }}
                        placeholder="Add category…"
                        className="bg-transparent pl-2.5 pr-1 py-1 text-xs text-gray-200 placeholder-gray-500 outline-none w-24"
                        data-testid="modal-new-category-input"
                      />
                      <button onClick={addCategory} className="p-1 mr-0.5 text-emerald-400 hover:text-emerald-300" title="Add">
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-5">
                  {/* Show loading indicator if breakdown is being generated */}
                  {breakdownLoading && !breakdown && (
                    <section className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4">
                      <div className="flex items-center gap-3 text-purple-300">
                        <Loader2 size={16} className="animate-spin" />
                        <span className="text-sm">Generating AI breakdown...</span>
                      </div>
                    </section>
                  )}
                  
                  {breakdown && (
                    <section className="bg-[#0C0E13] border border-white/10 rounded-xl overflow-hidden">
                      {/* Header */}
                      <div className="bg-gradient-to-r from-purple-900/30 to-purple-800/10 border-b border-white/10 px-5 py-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-white flex items-center gap-2">
                            <Sparkles size={14} className="text-purple-400" /> AI Analysis
                          </h4>
                          {breakdown.meeting_effectiveness?.sentiment && (
                            <span className="text-[10px] uppercase tracking-wider text-purple-200 bg-purple-500/20 border border-purple-400/30 px-2 py-0.5 rounded-full">
                              {breakdown.meeting_effectiveness.sentiment}
                            </span>
                          )}
                          {/* Fallback for old format */}
                          {!breakdown.meeting_effectiveness?.sentiment && breakdown.sentiment && (
                            <span className="text-[10px] uppercase tracking-wider text-purple-200 bg-purple-500/20 border border-purple-400/30 px-2 py-0.5 rounded-full">
                              {breakdown.sentiment}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="p-5 space-y-5">
                        {/* Executive Summary / Key Principle Box */}
                        {(breakdown.executive_summary || breakdown.key_principle) && (
                          <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                            {breakdown.executive_summary && (
                              <p className="text-sm text-gray-200 leading-relaxed">{breakdown.executive_summary}</p>
                            )}
                            {breakdown.key_principle && (
                              <div className="mt-3 pt-3 border-t border-purple-500/20">
                                <div className="text-[10px] uppercase tracking-wider text-purple-400 mb-1">Key Principle</div>
                                <p className="text-sm text-purple-200 italic">&ldquo;{breakdown.key_principle}&rdquo;</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Fallback: Old overview format */}
                        {!breakdown.executive_summary && breakdown.overview && (
                          <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Overview</div>
                            <p className="text-sm text-gray-200 leading-relaxed">{breakdown.overview}</p>
                          </div>
                        )}
                        
                        {/* Context Row */}
                        {breakdown.context && (
                          <div className="flex flex-wrap gap-3">
                            {breakdown.context.meeting_type && (
                              <div className="bg-[#161A22] border border-white/10 rounded-lg px-3 py-2">
                                <div className="text-[9px] uppercase tracking-wider text-gray-500">Type</div>
                                <div className="text-sm text-white">{breakdown.context.meeting_type}</div>
                              </div>
                            )}
                            {breakdown.context.participants_count && (
                              <div className="bg-[#161A22] border border-white/10 rounded-lg px-3 py-2">
                                <div className="text-[9px] uppercase tracking-wider text-gray-500">Participants</div>
                                <div className="text-sm text-white">{breakdown.context.participants_count}</div>
                              </div>
                            )}
                            {breakdown.context.duration_assessment && (
                              <div className="bg-[#161A22] border border-white/10 rounded-lg px-3 py-2">
                                <div className="text-[9px] uppercase tracking-wider text-gray-500">Efficiency</div>
                                <div className="text-sm text-white">{breakdown.context.duration_assessment}</div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Topics Discussed - Table Style */}
                        {breakdown.topics_discussed?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                              <span className="w-1 h-3 bg-blue-500 rounded-full"></span> Topics Discussed
                            </div>
                            <div className="border border-white/10 rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-[#161A22] text-gray-400">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Topic</th>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Summary</th>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Outcome</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {breakdown.topics_discussed.map((t, i) => (
                                    <tr key={i} className={i % 2 === 0 ? 'bg-black/20' : ''}>
                                      <td className="px-3 py-2 text-white font-medium align-top">{t.topic}</td>
                                      <td className="px-3 py-2 text-gray-300 align-top">{t.summary}</td>
                                      <td className="px-3 py-2 text-gray-400 align-top">{t.outcome}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {/* Fallback: Old key_topics format */}
                        {!breakdown.topics_discussed && breakdown.key_topics?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Topics</div>
                            <div className="flex flex-wrap gap-1.5">
                              {breakdown.key_topics.map((t, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#161A22] border border-white/10 text-gray-300">{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Decisions Made - Table Style */}
                        {breakdown.decisions_made?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                              <span className="w-1 h-3 bg-emerald-500 rounded-full"></span> Decisions Made
                            </div>
                            <div className="border border-white/10 rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-[#161A22] text-gray-400">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Decision</th>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Rationale</th>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Owner</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {breakdown.decisions_made.map((d, i) => (
                                    <tr key={i} className={i % 2 === 0 ? 'bg-black/20' : ''}>
                                      <td className="px-3 py-2 text-white font-medium align-top">{d.decision}</td>
                                      <td className="px-3 py-2 text-gray-300 align-top">{d.rationale}</td>
                                      <td className="px-3 py-2 text-emerald-400 align-top">{d.owner}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {/* Fallback: Old key_decisions format */}
                        {!breakdown.decisions_made && breakdown.key_decisions?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Key Decisions</div>
                            <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                              {breakdown.key_decisions.map((d, i) => <li key={i}>{d}</li>)}
                            </ul>
                          </div>
                        )}
                        
                        {/* Action Items - Enhanced Table */}
                        {breakdown.action_items?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                              <span className="w-1 h-3 bg-orange-500 rounded-full"></span> Action Items
                            </div>
                            <div className="border border-white/10 rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-[#161A22] text-gray-400">
                                  <tr>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Action</th>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Owner</th>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Deadline</th>
                                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">Priority</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {breakdown.action_items.map((a, i) => {
                                    const action = typeof a === 'string' ? a : (a?.action || a?.text || '');
                                    const owner = (typeof a === 'object' && a) ? (a.owner || 'Unassigned') : 'Unassigned';
                                    const deadline = (typeof a === 'object' && a) ? (a.deadline || a.due || 'TBD') : 'TBD';
                                    const priority = (typeof a === 'object' && a) ? (a.priority || 'Medium') : 'Medium';
                                    const priorityColor = priority === 'High' ? 'text-red-400' : priority === 'Low' ? 'text-gray-500' : 'text-yellow-400';
                                    return (
                                      <tr key={i} className={i % 2 === 0 ? 'bg-black/20' : ''}>
                                        <td className="px-3 py-2 text-white align-top">{action}</td>
                                        <td className="px-3 py-2 text-orange-400 align-top">{owner}</td>
                                        <td className="px-3 py-2 text-gray-400 align-top">{deadline}</td>
                                        <td className={`px-3 py-2 align-top font-medium ${priorityColor}`}>{priority}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {/* Important Nuances - Callout Box */}
                        {breakdown.important_nuances?.length > 0 && (
                          <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4">
                            <div className="text-[10px] uppercase tracking-wider text-yellow-400 mb-2 flex items-center gap-2">
                              <AlertCircle size={12} /> Important Nuances
                            </div>
                            <ul className="space-y-1.5 text-sm text-yellow-100/80">
                              {breakdown.important_nuances.map((n, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <span className="text-yellow-500 mt-1">•</span>
                                  <span>{n}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Open Questions */}
                        {breakdown.open_questions?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                              <span className="w-1 h-3 bg-red-500 rounded-full"></span> Open Questions
                            </div>
                            <div className="space-y-2">
                              {breakdown.open_questions.map((q, i) => {
                                const question = typeof q === 'string' ? q : (q?.question || '');
                                const context = (typeof q === 'object' && q) ? q.context : null;
                                const suggestedOwner = (typeof q === 'object' && q) ? q.suggested_owner : null;
                                return (
                                  <div key={i} className="bg-[#161A22] border border-white/10 rounded-lg p-3">
                                    <p className="text-sm text-white">{question}</p>
                                    {context && <p className="text-xs text-gray-500 mt-1">{context}</p>}
                                    {suggestedOwner && <p className="text-xs text-red-400 mt-1">→ {suggestedOwner}</p>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Speaker Contributions */}
                        {(breakdown.speaker_contributions?.length > 0 || breakdown.speaker_breakdown?.length > 0) && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                              <span className="w-1 h-3 bg-purple-500 rounded-full"></span> Speaker Contributions
                            </div>
                            <div className="grid gap-3">
                              {(breakdown.speaker_contributions || breakdown.speaker_breakdown || []).map((sp, i) => (
                                <div key={i} className="bg-[#161A22] border border-white/10 rounded-lg p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-purple-300 font-medium text-sm">{sp.name}</span>
                                    {sp.role_in_meeting && (
                                      <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-300">{sp.role_in_meeting}</span>
                                    )}
                                  </div>
                                  {sp.key_points?.length > 0 && (
                                    <ul className="space-y-1 text-sm text-gray-300">
                                      {sp.key_points.map((pt, j) => (
                                        <li key={j} className="flex items-start gap-2">
                                          <span className="text-purple-500 mt-1">•</span>
                                          <span>{pt}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {/* Fallback for old format */}
                                  {sp.summary && !sp.key_points && (
                                    <p className="text-sm text-gray-300">{sp.summary}</p>
                                  )}
                                  {sp.expertise_shown && (
                                    <p className="text-xs text-gray-500 mt-2 italic">{sp.expertise_shown}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Follow-up Required */}
                        {breakdown.follow_up_required && (
                          <div className="grid grid-cols-3 gap-3">
                            {breakdown.follow_up_required.immediate?.length > 0 && (
                              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-red-400 mb-2">Immediate (24-48h)</div>
                                <ul className="space-y-1 text-xs text-red-200/80">
                                  {breakdown.follow_up_required.immediate.map((f, i) => <li key={i}>• {f}</li>)}
                                </ul>
                              </div>
                            )}
                            {breakdown.follow_up_required.this_week?.length > 0 && (
                              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-yellow-400 mb-2">This Week</div>
                                <ul className="space-y-1 text-xs text-yellow-200/80">
                                  {breakdown.follow_up_required.this_week.map((f, i) => <li key={i}>• {f}</li>)}
                                </ul>
                              </div>
                            )}
                            {breakdown.follow_up_required.longer_term?.length > 0 && (
                              <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                                <div className="text-[10px] uppercase tracking-wider text-blue-400 mb-2">Longer Term</div>
                                <ul className="space-y-1 text-xs text-blue-200/80">
                                  {breakdown.follow_up_required.longer_term.map((f, i) => <li key={i}>• {f}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Fallback: Old next_steps format */}
                        {!breakdown.follow_up_required && breakdown.next_steps?.length > 0 && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Next Steps</div>
                            <ul className="space-y-1 list-disc list-inside text-sm text-gray-200">
                              {breakdown.next_steps.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        
                        {/* Related Topics */}
                        {breakdown.related_topics?.length > 0 && (
                          <div className="pt-3 border-t border-white/10">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Related Topics for Future Meetings</div>
                            <div className="flex flex-wrap gap-1.5">
                              {breakdown.related_topics.map((t, i) => (
                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#161A22] border border-white/10 text-gray-400">{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  )}
                  {activeDetail.data.summary?.overview && !breakdown && (
                    <section>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Summary</h4>
                      <p className="text-sm text-gray-200 leading-relaxed">{activeDetail.data.summary.overview}</p>
                    </section>
                  )}
                  {(() => {
                    const ai = activeDetail.data.summary?.action_items;
                    const arr = Array.isArray(ai)
                      ? ai
                      : (typeof ai === 'string' && ai.trim())
                        ? ai.split(/\n+/).map(s => s.trim()).filter(Boolean)
                        : [];
                    if (arr.length === 0) return null;
                    return (
                      <section>
                        <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Action items</h4>
                        <ul className="space-y-1.5 list-disc list-inside text-sm text-gray-200">
                          {arr.map((a, i) => <li key={i}>{typeof a === 'string' ? a : (a?.text || JSON.stringify(a))}</li>)}
                        </ul>
                      </section>
                    );
                  })()}
                  {(activeDetail.data.sentences || []).length > 0 && (
                    <section>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Transcript</h4>
                      
                      {/* Show speakers at top if available but lines don't have speaker names */}
                      {(() => {
                        const speakers = activeDetail.data.speakers || [];
                        const hasLineSpeakers = activeDetail.data.sentences?.some(s => 
                          s.speaker_name && s.speaker_name !== 'Manual Upload' && s.speaker_name !== 'Unknown'
                        );
                        
                        // Also check AI breakdown for speakers
                        const breakdownSpeakers = breakdown?.speaker_contributions || breakdown?.speaker_breakdown || [];
                        const allSpeakerNames = [
                          ...speakers.map(s => typeof s === 'string' ? s : s.name),
                          ...breakdownSpeakers.map(s => s.name)
                        ].filter(Boolean);
                        const uniqueSpeakers = [...new Set(allSpeakerNames)];
                        
                        if (!hasLineSpeakers && uniqueSpeakers.length > 0) {
                          return (
                            <div className="mb-4 p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                              <div className="text-[10px] uppercase tracking-wider text-purple-400 mb-2">Participants in this meeting</div>
                              <div className="flex flex-wrap gap-2">
                                {uniqueSpeakers.map((name, idx) => (
                                  <span key={idx} className="text-sm px-2 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-200">
                                    {name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      <div className="space-y-3 text-sm">
                        {activeDetail.data.sentences.map((s, i) => {
                          // Don't show "Manual Upload" as speaker name - show actual speaker or nothing
                          const speakerName = s.speaker_name && s.speaker_name !== 'Manual Upload' && s.speaker_name !== 'Unknown' 
                            ? s.speaker_name 
                            : null;
                          
                          // If no speaker names in any line, show simpler format without the dash column
                          const hasAnySpeakers = activeDetail.data.sentences?.some(sent => 
                            sent.speaker_name && sent.speaker_name !== 'Manual Upload' && sent.speaker_name !== 'Unknown'
                          );
                          
                          if (!hasAnySpeakers) {
                            // Simpler format - just the text, no speaker column
                            return (
                              <div key={i} className="text-gray-200 leading-relaxed">
                                {s.text}
                              </div>
                            );
                          }
                          
                          return (
                            <div key={i} className="flex gap-3">
                              {speakerName ? (
                                <div className="w-28 flex-shrink-0 text-xs text-purple-300 font-medium">{speakerName}</div>
                              ) : (
                                <div className="w-28 flex-shrink-0 text-xs text-gray-600 font-medium">—</div>
                              )}
                              <div className="flex-1 text-gray-200 leading-relaxed">{s.text}</div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  {/* Speakers — inline rename */}
                  {(activeDetail.data.speakers || []).length > 0 && (
                    <section>
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Speakers</h4>
                      <div className="flex flex-wrap gap-2">
                        {activeDetail.data.speakers.map(sp => (
                          <div key={sp.id} className="flex items-center gap-1.5 bg-[#161A22] border border-white/10 rounded-full pl-3 pr-1.5 py-1">
                            {editingSpeakerId === sp.id ? (
                              <>
                                <input
                                  autoFocus
                                  value={editingSpeakerName}
                                  onChange={e => setEditingSpeakerName(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveSpeakerName(); if (e.key === 'Escape') setEditingSpeakerId(null); }}
                                  className="bg-transparent text-sm text-white outline-none w-28"
                                  data-testid={`speaker-input-${sp.id}`}
                                />
                                <button onClick={saveSpeakerName} className="p-1 text-emerald-400 hover:text-emerald-300"><Check size={12} /></button>
                              </>
                            ) : (
                              <>
                                <span className="text-sm text-gray-200">{sp.name || `Speaker ${sp.id}`}</span>
                                <button
                                  onClick={() => { setEditingSpeakerId(sp.id); setEditingSpeakerName(sp.name || ''); }}
                                  className="p-1 text-gray-400 hover:text-white"
                                  data-testid={`edit-speaker-${sp.id}`}
                                >
                                  <Pencil size={11} />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" 
          onClick={() => { if (!uploading) { setShowUploadModal(false); clearUpload(); setUploadMode('new'); } }}
        >
          <div 
            className="bg-[#0C0E13] border border-white/15 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0C0E13] border-b border-white/10 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Upload Transcriptions</h2>
                </div>
                <button 
                  onClick={() => { if (!uploading) { setShowUploadModal(false); clearUpload(); setUploadMode('new'); } }}
                  disabled={uploading}
                  className="text-gray-400 hover:text-white disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Mode Tabs */}
              <div className="flex gap-2">
                <button
                  data-testid="upload-mode-new-tab"
                  onClick={() => { setUploadMode('new'); clearUpload(); }}
                  disabled={uploading}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === 'new'
                      ? 'bg-purple-600 text-white'
                      : 'bg-[#161A22] text-gray-400 hover:text-white hover:bg-[#1E232D]'
                  }`}
                >
                  <Upload size={14} className="inline mr-2" />
                  New Transcriptions
                </button>
                <button
                  data-testid="upload-mode-attach-tab"
                  onClick={() => { setUploadMode('attach'); clearUpload(); }}
                  disabled={uploading}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === 'attach'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-[#161A22] text-gray-400 hover:text-white hover:bg-[#1E232D]'
                  }`}
                >
                  <Music size={14} className="inline mr-2" />
                  Attach Audio to Existing
                </button>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* Mode Description */}
              {uploadMode === 'new' ? (
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                  <p className="text-xs text-purple-300">
                    <strong>New Transcriptions:</strong> Upload audio files and PDFs to create new transcript records. Files with matching names will be combined.
                  </p>
                </div>
              ) : (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                  <p className="text-xs text-emerald-300">
                    <strong>Attach Audio:</strong> Upload audio files to automatically match and attach to existing transcriptions by title. Unmatched files will create new transcripts.
                  </p>
                </div>
              )}

              {/* Drag & Drop Zone */}
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  isDragging 
                    ? uploadMode === 'attach' ? 'border-emerald-500 bg-emerald-500/10' : 'border-purple-500 bg-purple-500/10'
                    : 'border-white/20 hover:border-white/40 hover:bg-white/5'
                }`}
                data-testid="upload-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={uploadMode === 'attach' ? '.mp3,.m4a,.wav,.webm,.mpeg,.mpga,.mp4,.ogg,.flac' : '.mp3,.m4a,.wav,.webm,.mpeg,.mpga,.mp4,.pdf'}
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                {uploadMode === 'attach' ? (
                  <Music size={32} className={`mx-auto mb-3 ${isDragging ? 'text-emerald-400' : 'text-gray-500'}`} />
                ) : (
                  <Upload size={32} className={`mx-auto mb-3 ${isDragging ? 'text-purple-400' : 'text-gray-500'}`} />
                )}
                <p className="text-sm text-gray-300 mb-1">
                  {isDragging ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
                </p>
                <p className="text-xs text-gray-500">
                  {uploadMode === 'attach' 
                    ? 'Audio files only: MP3, WAV, M4A, OGG, FLAC · Max 100MB per file'
                    : 'Supports MP3, WAV, M4A audio · PDF documents · Max 100MB per file'
                  }
                </p>
              </div>

              {/* File pairing info - only for new mode */}
              {uploadMode === 'new' && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-xs text-blue-300">
                    <strong>Tip:</strong> Files with matching names (e.g., <code className="bg-blue-500/20 px-1 rounded">meeting.mp3</code> + <code className="bg-blue-500/20 px-1 rounded">meeting.pdf</code>) will be combined into a single transcript record.
                  </p>
                </div>
              )}

              {/* Selected Files List - Grouped by stem */}
              {uploadFiles.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs uppercase tracking-wider text-gray-500">
                      Selected Files ({uploadFiles.length}) → {Object.keys(fileGroups).length} transcript{Object.keys(fileGroups).length !== 1 ? 's' : ''}
                    </h4>
                    {!uploading && (
                      <button 
                        onClick={clearUpload}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {Object.entries(fileGroups).map(([stem, group]) => {
                      const isCombined = group.audio && group.pdf;
                      return (
                        <div 
                          key={stem}
                          className={`bg-[#161A22] border rounded-lg p-3 ${
                            isCombined ? 'border-emerald-500/40' : 'border-white/10'
                          }`}
                        >
                          {isCombined && (
                            <div className="text-xs text-emerald-400 mb-2 flex items-center gap-1">
                              <CheckCircle2 size={12} />
                              Files will be combined into one transcript
                            </div>
                          )}
                          <div className="space-y-1.5">
                            {group.audio && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  <Music size={14} className="text-purple-400 flex-shrink-0" />
                                  <span className="text-sm text-gray-200 truncate">{group.audio.name}</span>
                                  <span className="text-xs text-gray-500">{formatFileSize(group.audio.size)}</span>
                                </div>
                                {!uploading && (
                                  <button 
                                    onClick={() => removeFile(group.audioIdx)}
                                    className="text-gray-500 hover:text-red-400 flex-shrink-0"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                            )}
                            {group.pdf && (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText size={14} className="text-blue-400 flex-shrink-0" />
                                  <span className="text-sm text-gray-200 truncate">{group.pdf.name}</span>
                                  <span className="text-xs text-gray-500">{formatFileSize(group.pdf.size)}</span>
                                </div>
                                {!uploading && (
                                  <button 
                                    onClick={() => removeFile(group.pdfIdx)}
                                    className="text-gray-500 hover:text-red-400 flex-shrink-0"
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">Processing files...</span>
                    <span className="text-purple-400">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-[#161A22] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Audio files are being transcribed using AI. This may take a moment...
                  </p>
                </div>
              )}

              {/* Upload Results */}
              {uploadResults && (
                <div className="space-y-3">
                  {/* New mode results */}
                  {uploadMode === 'new' && uploadResults.processed > 0 && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-emerald-300 mb-2">
                        <CheckCircle2 size={16} />
                        <span className="font-medium">{uploadResults.processed} transcript(s) created</span>
                      </div>
                      <div className="space-y-1.5">
                        {uploadResults.results?.map((r, i) => (
                          <div key={i} className="text-xs text-emerald-200/70 flex items-center gap-2">
                            <span className="font-medium">{r.title}</span>
                            {r.has_audio && <Music size={10} className="text-purple-400" />}
                            {r.has_pdf && <FileText size={10} className="text-blue-400" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Attach mode results - Matched */}
                  {uploadMode === 'attach' && uploadResults.results?.matched?.length > 0 && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-emerald-300 mb-2">
                        <CheckCircle2 size={16} />
                        <span className="font-medium">{uploadResults.results.matched.length} audio file(s) attached to existing transcripts</span>
                      </div>
                      <div className="space-y-1.5">
                        {uploadResults.results.matched.map((r, i) => (
                          <div key={i} className="text-xs text-emerald-200/70 flex items-center gap-2">
                            <Music size={10} className="text-purple-400" />
                            <span>{r.filename}</span>
                            <span className="text-gray-500">→</span>
                            <span className="font-medium">{r.transcript_title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Attach mode results - Unmatched (new transcripts created) */}
                  {uploadMode === 'attach' && uploadResults.results?.unmatched?.length > 0 && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-blue-300 mb-2">
                        <AlertCircle size={16} />
                        <span className="font-medium">{uploadResults.results.unmatched.length} new transcript(s) created (no matching title found)</span>
                      </div>
                      <div className="space-y-1.5">
                        {uploadResults.results.unmatched.map((r, i) => (
                          <div key={i} className="text-xs text-blue-200/70 flex items-center gap-2">
                            <Music size={10} className="text-purple-400" />
                            <span>{r.filename}</span>
                            <span className="text-gray-500">→ Created:</span>
                            <span className="font-medium">{r.new_title}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {uploadResults.errors?.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-red-300 mb-2">
                        <AlertCircle size={16} />
                        <span className="font-medium">{uploadResults.errors.length} file(s) failed</span>
                      </div>
                      <div className="space-y-1">
                        {uploadResults.errors.map((e, i) => (
                          <div key={i} className="text-xs text-red-200/70">
                            <span className="font-medium">{e.filename}:</span> {e.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Attach mode errors from results.errors */}
                  {uploadMode === 'attach' && uploadResults.results?.errors?.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-red-300 mb-2">
                        <AlertCircle size={16} />
                        <span className="font-medium">{uploadResults.results.errors.length} file(s) failed</span>
                      </div>
                      <div className="space-y-1">
                        {uploadResults.results.errors.map((e, i) => (
                          <div key={i} className="text-xs text-red-200/70">
                            <span className="font-medium">{e.filename}:</span> {e.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-[#0C0E13] border-t border-white/10 p-5 flex justify-end gap-3">
              <button
                onClick={() => { setShowUploadModal(false); clearUpload(); setUploadMode('new'); }}
                disabled={uploading}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white disabled:opacity-50"
              >
                {uploadResults ? 'Close' : 'Cancel'}
              </button>
              {!uploadResults && (
                <button
                  onClick={uploadMode === 'attach' ? handleAttachAudio : handleUpload}
                  disabled={uploading || uploadFiles.length === 0}
                  className={`inline-flex items-center gap-2 px-5 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    uploadMode === 'attach' 
                      ? 'bg-emerald-600 hover:bg-emerald-500'
                      : 'bg-purple-600 hover:bg-purple-500'
                  }`}
                  data-testid="upload-submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : uploadMode === 'attach' ? (
                    <>
                      <Music size={16} />
                      Attach Audio Files
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Upload & Transcribe
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transcriptions;
